<template>
  <div class="!landscape:max-lg:pt-0 flex h-full min-h-0 flex-col md:pt-2">
    <splitpanes
      class="default-theme min-h-0 overflow-y-auto"
      horizontal
      :dblClickSplitter="false"
    >
      <pane v-if="!onlyOldParagraph" :size="activeOldParagraphId ? 50 : 100">
        <Transition name="fade" mode="out-in">
          <ParagraphFullView
            v-if="fullView"
            class="h-full"
            :flatToc
            :legalText
            :legalTextParagraph
            :toolIsSelected="markingMode !== undefined"
            @delete="handleDelete"
            @move="handleMove"
            @editLink="handleEditLink"
            @selection="handleSelection"
          />
          <div
            v-else-if="loading"
            class="flex h-full w-full items-center justify-center"
          >
            <i-line-md-loading-twotone-loop class="h-12 w-12" />
          </div>
          <div
            v-else-if="legalTextParagraph"
            class="mb-auto h-full min-h-0 w-full"
            :class="{ 'select-none': markingMode === 'erasing' }"
          >
            <div :key="legalTextParagraph.id" class="h-full">
              <ParagraphPart
                :legalText="legalText"
                :legalTextParagraph="legalTextParagraph"
                :isMainContent="isMainContent"
                :showLinks="showLinks"
                @delete="handleDelete"
                @move="handleMove"
                @editLink="handleEditLink"
                @selection="handleSelection(legalTextParagraph, $event)"
              />
            </div>
          </div>
          <div
            v-else
            class="flex h-full w-full items-center justify-center text-lg"
          >
            <p>{{ t("missing_paragraph") }}</p>
          </div>
        </Transition>
      </pane>
      <pane
        v-if="activeOldParagraphId"
        :size="onlyOldParagraph ? 100 : 50"
        class="min-h-0"
      >
        <Transition name="fade" mode="out-in">
          <div
            v-if="oldParagraphLoading"
            class="flex h-full w-full grow items-center justify-center"
          >
            <i-line-md-loading-twotone-loop class="h-12 w-12" />
          </div>
          <div
            v-else-if="oldParagraph"
            class="mb-auto flex h-full min-h-0 w-full flex-col"
          >
            <ParagraphPart
              closable
              :legalText="oldParagraph.legal_text!"
              :legalTextParagraph="oldParagraph"
              :isMainContent="false"
              :showLinks="false"
              @delete="handleDelete"
              @move="handleMove"
              @editLink="handleEditLink"
              @selection="handleSelection(oldParagraph, $event)"
            />
          </div>
        </Transition>
      </pane>
    </splitpanes>
    <Transition name="fade" mode="out-in">
      <ParagraphHistory
        v-if="legalTextParagraph && oldParagraph"
        class="w-full"
        :active="activeOldParagraphId"
        :paragraph="legalTextParagraph"
      />
    </Transition>
    <ParagraphFooter
      v-if="legalText && legalTextParagraph"
      :legalText="legalText"
      :legalTextParagraph="legalTextParagraph"
    />
    <LinkModal
      v-if="legalText && linkModalParagraph"
      v-model="linkModalOpen"
      :saving="linkSaving"
      :legalText="legalText"
      :paragraph="linkModalParagraph"
      :editingLinkChain="editingLinkChain"
      @closed="handleLinkModalOnClose"
      @save="handleSaveLinkChain"
    />
  </div>
</template>

<script lang="ts" setup>
import type {
  ILinkChain,
  IMark,
  INewUserEntry,
  INote,
  IParagraphEntry,
  ISchema,
  IUpdatableLinkChain,
} from "~/models/IUserEntry";
import type { ILegalText } from "~/models/ILegalText";
import type { ILegalTextParagraph } from "~/models/ILegalTextParagraph";
import type { IPosition } from "~/models/IPosition";
import type { ColorPicker } from "~/types/ColorPicker";
import { Pane, Splitpanes } from "splitpanes";
import type { IToC } from "~/models/IToC";

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<{
  flatToc: (IToC | ILegalTextParagraph)[];
  legalText: ILegalText;
  legalTextParagraph: ILegalTextParagraph | undefined;
  markerColor: ColorPicker;
  markingMode?: IParagraphEntry["type"] | "erasing";
  markerPermanent?: boolean;
  nextLegalTextParagraph?: string;
  previousLegalTextParagraph?: string;
  isMainContent?: boolean;
  showLinks?: boolean;
  movingEntry?: IParagraphEntry;
  loading: boolean;
  fullView: boolean;
}>();

const { oldParagraph, oldParagraphLoading } = useOldParagraphState();

const activeOldParagraphId = computed(() => oldParagraph.value?.id);

const emits = defineEmits<{
  "update:movingEntry": [IParagraphEntry | undefined];
  "update:markingMode": [IParagraphEntry["type"] | undefined | "erasing"];
}>();

const {
  legalText,
  legalTextParagraph,
  previousLegalTextParagraph,
  nextLegalTextParagraph,
  markerPermanent,
} = toRefs(props);

const { t } = useI18n();

useKeyboardParagraphNavigation(
  () => legalText.value.slug,
  previousLegalTextParagraph,
  nextLegalTextParagraph
);

const movingEntry = useVModel(props, "movingEntry", emits);

const { markerColor, markingMode } = useVModels(props, emits);

const { provideErasingMode } = useEraser();
provideErasingMode(() => markingMode.value === "erasing");

const { saveEntry, moveEntry } = useEntries();

const lastSavedEntryStore = useLastSavedEntryStore();
const { setLastSavedEntry } = lastSavedEntryStore;
const { lastSavedEntry } = storeToRefs(lastSavedEntryStore);

const selectionStore = useSelectionStore();

const { isCollapsed, end, start } = storeToRefs(selectionStore);

const toast = useToast();

const { linkSaving, saveLinkChain } = useLinkChain();

const editingLinkChain = ref<IUpdatableLinkChain>();
const linkModalOpen = ref(false);
const linkModalParagraph = ref<ILegalTextParagraph>();

const openLinkModal = (options: {
  paragraph?: ILegalTextParagraph;
  chain?: IUpdatableLinkChain;
}) => {
  editingLinkChain.value = options.chain;

  const paragraph = options.chain?.legal_text_paragraph ?? options.paragraph;

  if (!paragraph) {
    throw new Error("Paragraph not found trying to open link modal");
  }

  const onOldVersion = Boolean(paragraph.valid_until);

  linkModalParagraph.value =
    onOldVersion && oldParagraph.value ? oldParagraph.value : paragraph;

  linkModalOpen.value = true;
};

const handleLinkModalOnClose = () => {
  if (!markerPermanent?.value && markingMode) {
    markingMode.value = undefined;
  }
  selectionStore.resetSelection();
  editingLinkChain.value = undefined;
};

const handleSaveLinkChain = async (
  paragraph: ILegalTextParagraph,
  links: IUpdatableLinkChain["links"]
) => {
  await saveLinkChain(paragraph, {
    ...(editingLinkChain.value ?? payload.value),
    links,
  });
  linkModalOpen.value = false;
};

const origin = ref<IPosition["origin"]>("text");

const payload = computed(() => {
  let obj: {
    type?: IParagraphEntry["type"];
    position: Partial<IPosition>;
  } = {
    position: { origin: origin.value },
    type: markingMode?.value as IParagraphEntry["type"],
  };
  switch (origin.value) {
    case "headline":
      break;
    case "text":
      obj = {
        ...obj,
        position: {
          ...obj.position,
          node_start_id: start.value?.id,
          word_start_offset: start.value?.offset,
          node_end_id: end.value?.id,
          word_end_offset: end.value?.offset,
        },
      };
      break;
    default:
      break;
  }
  if (markingMode?.value === "mark") {
    (obj as IMark).color = markerColor.value.color;
    (obj as IMark).color_type = markerColor.value.color_type;
  }
  return obj as INewUserEntry;
});

const { setActive } = useSetActiveEntry();

const { editMode } = storeToRefs(useEditorStore());

const { active } = useFetchActiveEntry();

// reset edit mode when active entry changes but only if it wasn't just created
watch(active, (newActive, oldActive) => {
  if (
    oldActive?.id !== newActive?.id &&
    newActive?.id !== lastSavedEntry.value?.id &&
    newActive?.type === "schema"
  ) {
    editMode.value = false;
  }
});

whenever(lastSavedEntry, async (entry) => {
  // yes, this is intentional!
  // the first time settings editMode works when we have **no** schema open
  editMode.value = true;
  await setActive(entry as ISchema | INote);
  // and this makes it so that if we create a schema while another one is opened we switch in editMode
  editMode.value = true;
});

const setSelection = () => {
  selectionStore.setSelection();

  if (selectionStore.isCollapsed && !markingMode?.value) {
    selectionStore.resetSelection();
  }
};

const intervalUpdateSelection = useIntervalFn(setSelection, 500, {
  immediate: false,
});

const handleSave = async (paragraph: ILegalTextParagraph) => {
  if (markingMode?.value === "erasing") {
    //we don't save anything when it's on erasing mode
    selectionStore.resetSelection();
    return;
  }
  if (movingEntry.value) {
    try {
      await moveEntry(movingEntry.value, payload.value.position);
    } catch (_) {
      toast.error(t("legal_text.textEditor.errors.marking_could_not_be_moved"));
    }
    if (markingMode) {
      markingMode.value = undefined;
    }
    movingEntry.value = undefined;
  }
  // for links we'll display a popup asking the user where the link should point to
  else if (markingMode?.value === "link_chain") {
    intervalUpdateSelection.pause();
    return openLinkModal({ paragraph });
  } else {
    // for everything else, just save it
    try {
      const entry = await saveEntry(paragraph, payload.value);

      if (entry.type === "schema" || entry.type === "note") {
        setLastSavedEntry(entry);
      }
      if (!markerPermanent?.value && markingMode) {
        markingMode.value = undefined;
      }
    } catch (e: any) {
      if (e?.message !== "LIMIT_REACHED") {
        toast.error(
          t("legal_text.textEditor.errors.marking_could_not_be_added")
        );
        console.error(e);
      }
    }
  }
  selectionStore.resetSelection();
};

const paragraphToSave = ref<ILegalTextParagraph>();

const handleSelection = (paragraph: ILegalTextParagraph, touch: boolean) => {
  if (markingMode.value === "erasing") {
    const sel = window.getSelection();
    if (sel?.type.toLowerCase() === "range") {
      selectionStore.resetSelection();
      toast.info(
        "Klicke auf die zu löschende Personalisierung, anstatt sie zu markieren."
      );
    }
    return;
  }
  paragraphToSave.value = paragraph;
  selectionStore.setTouch(touch);
  // we need a timeout because mobile browsers don't set the selection immediately
  setTimeout(setSelection, selectionStore.isTouch ? 100 : 0);
};

const guardSave = () => {
  // set end = start for links, notes and schemas
  if (["link_chain", "note", "schema"].includes(markingMode?.value ?? "")) {
    end.value = start.value;
  } else if (
    selectionStore.isCollapsed ||
    JSON.stringify(start.value) === JSON.stringify(end.value)
  ) {
    // do nothing if no range has been selected when using underline or marker tool
    selectionStore.resetSelection();
    return false;
  }
  return true;
};

watch(
  () => selectionStore.isTouch,
  (touch) => {
    if (touch) {
      intervalUpdateSelection.resume();
    } else {
      intervalUpdateSelection.pause();
    }
  }
);

whenever(logicAnd(start, end, markingMode, paragraphToSave), async () => {
  if (!guardSave()) {
    return;
  }
  await handleSave(paragraphToSave.value!);
});

const handleMove = (entry: ILinkChain | INote | ISchema) => {
  selectionStore.resetSelection();
  movingEntry.value = entry;
  if (markingMode) {
    markingMode.value = entry.type;
  }
};

watch(
  () => markingMode?.value,
  () => {
    if (isCollapsed.value) {
      selectionStore.resetSelection();
    }
  }
);

const handleEditLink = (chain: ILinkChain) => {
  if (markingMode) {
    markingMode.value = undefined;
  }
  openLinkModal({ chain });
};

const { isSmallerThanLg } = useLexMeaTailwindBreakpoints();

const onlyOldParagraph = logicAnd(isSmallerThanLg, activeOldParagraphId);

const { open: openDeleteModal } = useEntryDeleteModal();
const { deleteEntry } = useDeleteEntry();

const handleDelete = async (entry: IParagraphEntry) => {
  if (!entry.editable) return;

  if (["underline", "mark"].includes(entry.type)) {
    await deleteEntry(entry);
  } else {
    openDeleteModal(entry);
  }
};
</script>

<i18n>
{
  "de": {
    "missing_paragraph": "Paragraph nicht gefunden"
  },
  "en": {
    "missing_paragraph": "Paragraph not found"
  }
}
</i18n>
